/**
 * Application Constants
 *
 * Centralized constants to replace magic numbers across the codebase
 * Issue #8: Extract hardcoded values for better maintainability
 */

// ===== CoinGecko API Configuration =====
// In development: Uses VITE_COINGECKO_API_KEY for direct API calls
// In production: Uses Vercel serverless proxy (see api.ts for implementation)
const isDev = import.meta.env.DEV;
const COINGECKO_API_KEY = isDev ? (import.meta.env.VITE_COINGECKO_API_KEY as string | undefined) : undefined;
export const COINGECKO_CONFIG = {
  API_KEY: COINGECKO_API_KEY,
  BASE_URL: COINGECKO_API_KEY
    ? 'https://pro-api.coingecko.com/api/v3'
    : 'https://api.coingecko.com/api/v3',
  // In production, we assume Pro via serverless proxy
  IS_PRO: !isDev || !!COINGECKO_API_KEY,
  USE_PROXY: !isDev, // In production, always use proxy
  // Helper to append API key to URL (development only)
  withKey: (url: string): string => {
    if (!COINGECKO_API_KEY) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}x_cg_pro_api_key=${COINGECKO_API_KEY}`;
  },
};

// ===== API Configuration =====
export const API_CONFIG = {
  BACKEND_URL: import.meta.env.VITE_BACKEND_URL || 'http://localhost:3002',
  COINGECKO_URL: COINGECKO_CONFIG.BASE_URL,

  // Retry configuration
  RETRY_ATTEMPTS: 3,
  RETRY_BASE_DELAY: 1000, // 1 second
  RETRY_MAX_DELAY: 10000, // 10 seconds

  // Cache configuration
  CACHE_TTL_MS: 15 * 60 * 1000, // 15 minutes

  // Logging configuration
  LOG_MAX_ENTRIES: 500,
  LOG_MAX_AGE_MS: 24 * 60 * 60 * 1000, // 24 hours
} as const;

// ===== Time Constants =====
export const TIME = {
  SECOND: 1000,
  MINUTE: 60 * 1000,
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
  WEEK: 7 * 24 * 60 * 60 * 1000,
} as const;

// ===== Portfolio Defaults =====
export const PORTFOLIO_DEFAULTS = {
  MOCK_BASE_VALUE: 120000,
  MOCK_VARIANCE: 30000,
} as const;

// ===== CoinGecko ID Mapping =====
// Maps token symbols to CoinGecko coin IDs for backtesting and historical data
export const COINGECKO_ID_MAP: Record<string, string> = {
  'BTC': 'bitcoin',
  'ETH': 'ethereum',
  'SOL': 'solana',
  'ADA': 'cardano',
  'DOT': 'polkadot',
  'MATIC': 'matic-network',
  'POLYGON': 'matic-network',
  'AVAX': 'avalanche-2',
  'LINK': 'chainlink',
  'UNI': 'uniswap',
  'ATOM': 'cosmos',
  'XRP': 'ripple',
  'DOGE': 'dogecoin',
  'LTC': 'litecoin',
  'BCH': 'bitcoin-cash',
  'ALGO': 'algorand',
  'XLM': 'stellar',
  'VET': 'vechain',
  'FIL': 'filecoin',
  'THETA': 'theta-token',
  'TRX': 'tron',
  'EOS': 'eos',
  'XTZ': 'tezos',
  'AAVE': 'aave',
  'NEAR': 'near',
  'FTM': 'fantom',
  'SAND': 'the-sandbox',
  'MANA': 'decentraland',
  'AXS': 'axie-infinity',
  'RUNE': 'thorchain',
  'EGLD': 'elrond-erd-2',
  'HBAR': 'hedera-hashgraph',
  'ZEC': 'zcash',
  'DASH': 'dash',
  'XMR': 'monero'
} as const;

// ===== Bot Configuration Limits =====
export const BOT_LIMITS = {
  MAX_POSITION_SIZE_MIN: 5,
  MAX_POSITION_SIZE_MAX: 50,
  STOP_LOSS_MIN: 3,
  STOP_LOSS_MAX: 25,
  TAKE_PROFIT_MIN: 10,
  TAKE_PROFIT_MAX: 100,
  RISK_TOLERANCE_MIN: 1,
  RISK_TOLERANCE_MAX: 10,
} as const;

// ===== Performance Budgets =====
export const PERFORMANCE_TARGETS = {
  FIRST_CONTENTFUL_PAINT_MS: 1500,
  LARGEST_CONTENTFUL_PAINT_MS: 2500,
  TIME_TO_INTERACTIVE_MS: 3500,
  CUMULATIVE_LAYOUT_SHIFT: 0.1,
  FIRST_INPUT_DELAY_MS: 100,
} as const;

// ===== UI Constants =====
export const UI = {
  TOAST_DURATION_MS: 5000,
  MODAL_ANIMATION_DURATION_MS: 300,
  DEBOUNCE_SEARCH_MS: 300,
  THROTTLE_SCROLL_MS: 100,
} as const;
